import type { State } from '@energy-retailer/constants';
import { CustomerType, EnergyType } from '@energy-retailer/constants';
import { z } from 'zod';
import find from 'lodash/find';
import { pcodeToDistrib, postCodeMapping } from './mapping';
import distributorList from './data/distributors.json';
import { businessNTCs, residentialNTCs } from '../ntc-schema';
import { createSlug } from '../utils/create-slug';

const imageSchema = z
  .object({
    src: z.string(),
    alt: z.string(),
  })
  .optional()
  .default({
    src: '',
    alt: '',
  });

export const sharedDistributorSchema = z.object({
  name: z.string(),
  url: z.string(),
  contact: z
    .object({
      general: z.string(),
      faults: z.string(),
    })
    .partial(),
  image: imageSchema,
  id: z.string(),
  energyType: z.nativeEnum(EnergyType),
});

export const electricityDistributorSchema = sharedDistributorSchema.extend({
  energyType: z.literal(EnergyType.Electricity),
  meter: z.object({
    [CustomerType.Residential]: z
      .enum(residentialNTCs)
      .transform((networkTariffCode) => [
        {
          controlledLoad: 'NO',
          networkTariffCode,
          installationTypeCode: 'UNKNOWN',
        },
      ]),
    [CustomerType.Business]: z
      .enum(businessNTCs)
      .transform((networkTariffCode) => [
        {
          controlledLoad: 'NO',
          networkTariffCode,
          installationTypeCode: 'UNKNOWN',
        },
      ]),
  }),
});

export const gasDistributorSchema = sharedDistributorSchema.extend({
  energyType: z.literal(EnergyType.Gas),
});

export const distributorSchema = z.discriminatedUnion('energyType', [
  electricityDistributorSchema,
  gasDistributorSchema,
]);

export const distributorsSchema = z.array(distributorSchema);

export type Distributor = z.infer<typeof distributorSchema>;

export type ElectricityDistributorSchema = z.infer<
  typeof electricityDistributorSchema
>;

export const postCodeToState = z
  .string()
  .transform((value): State => postCodeMapping[value]);

export type GasDistributorSchema = z.infer<typeof gasDistributorSchema>;

export const getDistributorByID = z.string().transform((id): Distributor => {
  const distributor = find(distributorList, { id }) as Distributor;

  if (distributor) {
    return distributor;
  }

  throw new Error(`Error mapping distributor; unknown ID: ${id}`);
});

export const getDistributorByName = z
  .object({
    name: z.string(),
    energyType: z.nativeEnum(EnergyType),
  })
  .transform(({ name, energyType }): Distributor | undefined => {
    const nameToCheck = createSlug.parse(name);

    return (distributorList as Distributor[]).find((value) => {
      // normalise the name
      const normalisedName = createSlug.parse(value.name);

      if (normalisedName === nameToCheck && value.energyType === energyType) {
        return value;
      }
    });
  });

export const getDistributorsByPostCode = z.coerce
  .number()
  .transform((value): Distributor[] | undefined => {
    return pcodeToDistrib[value] ?? [];
  });
